import React from "react";
import {
  Container,
  Paper,
  Typography,
  Box,
  Divider,
  Alert,
  AlertTitle,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Grid,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import {
  Info,
  Warning,
  CheckCircle,
  Settings,
  Business,
  Cancel,
  AttachMoney,
  ExpandMore,
  Rule,
  Calculate,
} from "@mui/icons-material";
import { getSessionUser } from "../../utils/users";
import { Empresas } from "../../utils/empresa";

const deveExibirSecaoGarantias = () => {
  const params = new URLSearchParams(window.location.search);
  if (params.get("garantias") === "1") return true;
  const usuario = getSessionUser();
  return usuario?.empresa === Empresas.Rdp;
};

export const InstrucoesParametrizacao = () => {
  const mostrarGarantias = deveExibirSecaoGarantias();

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box display="flex" alignItems="center" mb={3}>
          <Info color="primary" sx={{ fontSize: 40, mr: 2 }} />
          <Typography variant="h4" component="h1" color="primary">
            Manual de Parametrização de Crédito
          </Typography>
        </Box>

        <Typography variant="subtitle1" color="text.secondary" paragraph>
          Este guia tem como objetivo orientar o preenchimento das configurações
          de análise de crédito. As regras definidas aqui alimentam o nosso
          motor de decisão e garantem que as avaliações sigam estritamente as
          políticas comerciais da empresa.
        </Typography>

        <Alert severity="warning" sx={{ mb: 4 }}>
          <AlertTitle>Atenção</AlertTitle>
          Todas as alterações feitas nesta tela impactam{" "}
          <strong>diretamente e imediatamente</strong> a aprovação ou reprovação
          de limites de crédito para seus clientes. Após concluir suas edições,
          não se esqueça de rolar até o final da página e clicar no botão{" "}
          <strong>Salvar Configurações</strong>.
        </Alert>

        {/* SEÇÃO 1: DADOS GERAIS E CMA */}
        <Box mb={4}>
          <Typography
            variant="h5"
            gutterBottom
            sx={{ display: "flex", alignItems: "center" }}
          >
            <Business sx={{ mr: 1 }} /> 1. Dados da Empresa e CMA
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Typography paragraph>
            <strong>Dados da Empresa:</strong> Tratam-se das informações
            cadastrais básicas da sua organização (Razão Social, CNPJ,
            Endereço). Mantenha sempre atualizado, pois estes dados podem ser
            exibidos em relatórios gerados.
          </Typography>

          <Typography paragraph>
            <strong>CMA (Critérios Mínimos de Aceitação):</strong> O CMA atua
            como uma barreira inicial ("Filtro de Corte"). Se uma destas regras
            estiver ativada e o cliente avaliado não passar nela, a análise de
            crédito será automaticamente <strong>REPROVADA</strong>, mesmo que a
            pontuação (Score) do cliente seja excelente.
          </Typography>

          <List dense>
            <ListItem>
              <ListItemIcon>
                <Cancel color="error" />
              </ListItemIcon>
              <ListItemText
                primary="Recusa Empresa não ativa na Receita"
                secondary="Exemplo: Se a empresa cliente estiver com o CNPJ 'Baixado', 'Inapto' ou 'Suspenso', o sistema nega o crédito imediatamente."
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <Cancel color="error" />
              </ListItemIcon>
              <ListItemText
                primary="Recusa Empresa em Recuperação Judicial"
                secondary="Impede a aprovação de clientes que possuam processos de recuperação judicial apontados nos órgãos de proteção."
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <Cancel color="error" />
              </ListItemIcon>
              <ListItemText
                primary="Tempo mínimo de fundação"
                secondary="Exemplo: Se configurado para 12 meses, empresas recém-abertas (com 6 meses de CNPJ) serão automaticamente recusadas."
              />
            </ListItem>
          </List>
        </Box>

        {/* SEÇÃO 2: PORTES */}
        <Box mb={4}>
          <Typography
            variant="h5"
            gutterBottom
            sx={{ display: "flex", alignItems: "center" }}
          >
            <Settings sx={{ mr: 1 }} /> 2. Configuração de Portes
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Typography paragraph>
            Para que a análise seja justa, não podemos avaliar um pequeno
            comércio com as mesmas exigências de uma multinacional. Para isso
            existem os <strong>Portes</strong>. O sistema enquadra o cliente
            analisado em um porte específico baseado em seu Faturamento ou
            Capital Social.
          </Typography>

          <Alert severity="info" sx={{ mb: 3 }}>
            <AlertTitle>Regra de Ouro dos Intervalos</AlertTitle>
            Os valores informados nos campos "De" e "Até"{" "}
            <strong>nunca podem se cruzar ou sobrepor</strong>.
            <br />
            <br />
            <strong>Exemplo de preenchimento correto:</strong>
            <ul>
              <li>
                <strong>PME:</strong> Faturamento De: R$ 0,00 | Até: R$
                1.000.000,00
              </li>
              <li>
                <strong>Corporate:</strong> Faturamento De: R$ 1.000.001,00 |
                Até: R$ 50.000.000,00
              </li>
            </ul>
          </Alert>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Paper
                variant="outlined"
                sx={{ p: 2, bgcolor: "background.default" }}
              >
                <Typography variant="subtitle2" gutterBottom>
                  <strong>Entendendo as Colunas da Tabela de Portes:</strong>
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <AttachMoney fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Faturamento (De / Até)"
                      secondary="É o intervalo principal. Representa a receita bruta ou faturamento anual do cliente."
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Business fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Capital Social (De / Até)"
                      secondary="Caso não tenhamos o faturamento do cliente, o sistema usará o capital social registrado no CNPJ para tentar enquadrá-lo no porte correto."
                    />
                  </ListItem>
                </List>
              </Paper>
            </Grid>
          </Grid>
        </Box>

        {/* SEÇÃO 3: INDICADORES E PESOS */}
        <Box mb={4}>
          <Typography
            variant="h5"
            gutterBottom
            sx={{ display: "flex", alignItems: "center" }}
          >
            <Rule sx={{ mr: 1 }} /> 3. Pesos dos Indicadores
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Typography paragraph>
            Nesta seção, você decide o quanto cada indicador "pesa" na nota
            final do cliente.
          </Typography>

          <Accordion
            defaultExpanded
            sx={{
              border: 1,
              borderColor: "divider",
              boxShadow: "none",
              "&:before": { display: "none" },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography fontWeight="bold" display="flex" alignItems="center">
                <Calculate sx={{ mr: 1 }} /> A Regra Matemática dos 100%
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography paragraph>
                Para que a nota final (Score) seja calculada corretamente, a
                soma vertical de todos os indicadores em uma coluna{" "}
                <strong>deve ser exatamente 100%</strong>. Se a soma ficar em
                90% ou 110%, o sistema não permitirá o salvamento das regras.
              </Typography>

              <Typography variant="subtitle2" gutterBottom mt={2}>
                Existem 4 cenários (colunas) que precisam ser distribuídos:
              </Typography>

              <Grid container spacing={2} mt={1}>
                <Grid item xs={12} sm={6} md={3}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      border: 1,
                      borderColor: "divider",
                      height: "100%",
                    }}
                  >
                    <Chip
                      label="Com Doc."
                      color="primary"
                      size="small"
                      sx={{ mb: 1 }}
                    />
                    <Typography variant="body2">
                      Análise de um cliente novo que <strong>enviou</strong>{" "}
                      documentos financeiros (como DRE ou Balanço Patrimonial).
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      border: 1,
                      borderColor: "divider",
                      height: "100%",
                    }}
                  >
                    <Chip
                      label="Sem Doc."
                      color="secondary"
                      size="small"
                      sx={{ mb: 1 }}
                    />
                    <Typography variant="body2">
                      Análise de um cliente novo que <strong>não enviou</strong>{" "}
                      documentação. O peso das consultas aos bureaus (Serasa/Boa
                      Vista) costuma ser maior aqui.
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      border: 1,
                      borderColor: "divider",
                      height: "100%",
                    }}
                  >
                    <Chip
                      label="Com Doc. Rel. Def."
                      color="success"
                      size="small"
                      sx={{ mb: 1 }}
                    />
                    <Typography variant="body2">
                      Análise de um <strong>cliente antigo</strong> da casa (com
                      histórico interno) que enviou documentação atualizada.
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      border: 1,
                      borderColor: "divider",
                      height: "100%",
                    }}
                  >
                    <Chip
                      label="Sem Doc. Rel. Def."
                      color="default"
                      size="small"
                      sx={{ mb: 1 }}
                    />
                    <Typography variant="body2">
                      Análise de um <strong>cliente antigo</strong>, porém
                      avaliado apenas via bureaus, sem documentação nova.
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {mostrarGarantias && (
            <Accordion
              sx={{
                mt: 2,
                border: 1,
                borderColor: "primary.main",
                boxShadow: "none",
                "&:before": { display: "none" },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography fontWeight="bold" color="primary">
                  Regras Específicas de Garantias (RDP)
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography paragraph>
                  O critério <strong>Garantias</strong> aparece listado junto
                  com os demais indicadores. O percentual que você preencher
                  para ele na tabela de pesos servirá como a "Nota Máxima" que
                  as garantias podem dar.
                </Typography>

                <Alert severity="info" variant="outlined" sx={{ mb: 3 }}>
                  <strong>Exemplo do Motor de Cálculo:</strong>
                  <br />
                  Pontuação = (Peso definido na tabela) × (Fator da combinação
                  escolhida) × (% de cobertura / 100).
                  <br />
                  <em>
                    * Caso o operador marque "Não" na pergunta "Tem Garantia?",
                    essa pontuação zera.
                  </em>
                </Alert>

                <Typography variant="subtitle2" gutterBottom>
                  Painel de Composição e Fatores:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircle fontSize="small" color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Cadastro Manual"
                      secondary="Você pode criar composições de garantias mistas selecionando os tipos na lista suspensa e definindo um fator multiplicador que vai de 0.00 até 1.00."
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircle fontSize="small" color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Combinações Padrão"
                      secondary="Para facilitar, o botão 'Carregar padrões RDP' já embute as regras matrizes aceitas automaticamente."
                    />
                  </ListItem>
                </List>
              </AccordionDetails>
            </Accordion>
          )}
        </Box>

        {/* SEÇÃO 4: VALORES FINANCEIROS E RESTRITIVOS */}
        <Box mb={2}>
          <Typography
            variant="h5"
            gutterBottom
            sx={{ display: "flex", alignItems: "center" }}
          >
            <AttachMoney sx={{ mr: 1 }} /> 4. Restritivos e Classificação
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Typography paragraph>
            Nesta etapa, definimos limites e gatilhos de tolerância que podem
            zerar a nota de uma seção da análise ou, pelo contrário, turbinar a
            nota devido a um bom relacionamento.
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 3, height: "100%" }}>
                <Typography variant="h6" gutterBottom color="error">
                  Limites de Restritivos (Bureaus)
                </Typography>
                <Typography variant="body2" paragraph>
                  Aqui você configura o máximo de dívidas (protestos, cheques
                  sem fundo, pendências) que está disposto a relevar antes de a
                  nota do cliente despencar.
                </Typography>

                <List dense>
                  <ListItem>
                    <ListItemText
                      primary="Quantidade Máxima"
                      secondary="Exemplo: Se preenchido como '5', e o Serasa retornar 6 protestos, o cliente perde todos os pontos referentes aos apontamentos."
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Valor Máximo Acumulado (R$)"
                      secondary="Exemplo: Se preenchido como R$ 10.000,00, e a dívida somada do cliente for R$ 12.000, a nota será afetada. Note que o campo aceita centavos separando com ponto ou vírgula."
                    />
                  </ListItem>
                </List>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper
                variant="outlined"
                sx={{ p: 3, height: "100%", borderColor: "success.main" }}
              >
                <Typography variant="h6" gutterBottom color="success.main">
                  Classificação Interna
                </Typography>
                <Typography variant="body2" paragraph>
                  A classificação interna é um sistema de bônus focado no{" "}
                  <strong>histórico comportamental do seu cliente</strong>{" "}
                  dentro da sua empresa. Se ele é um bom pagador, o CNPJ dele
                  ganha pontos extras; se tem histórico de atrasos, a nota cai.
                </Typography>

                <Alert severity="success" sx={{ mt: 2 }}>
                  <strong>Exemplo de Tabela de Classificação:</strong>
                  <br />
                  <br />
                  <strong>🟢 Perfil Ótimo (Bônus Alto)</strong>
                  <br />
                  <strong>Nota 12</strong> (Soma 12 pts no score final)
                  <br />
                  <br />
                  <strong>🟡 Perfil Bom (Bônus Médio)</strong>
                  <br />
                  <strong>Nota 8</strong> (Soma 8 pts)
                  <br />
                  <br />
                  <strong>🔴 Perfil Ruim (Baixo/Penalidade)</strong>
                  <br />
                  <strong>Nota 4</strong> (Soma apenas 4 pts)
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Container>
  );
};
